import { useEffect, useMemo } from 'react';

import { LinksFunction } from '@remix-run/node';
import {
  Links,
  Meta,
  MetaFunction,
  Outlet,
  Scripts,
  ScrollRestoration,
  useFetchers,
  useNavigation,
  useRouteError,
} from '@remix-run/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import NProgress from 'nprogress';
import nProgressStylesheet from 'nprogress/nprogress.css?url';
import reactDayPickerStylesheet from 'react-day-picker/dist/style.css?url';

import { Toaster } from './components/ui/toaster';
import globalStylesheet from './global.css?url';

export const links: LinksFunction = () => [
  { rel: 'stylesheet', href: globalStylesheet },
  { rel: 'stylesheet', href: reactDayPickerStylesheet },
  { rel: 'stylesheet', href: nProgressStylesheet },
  {
    rel: 'icon',
    href: '/favicon.ico',
  },
];

export const meta: MetaFunction = () => {
  return [{ title: 'Best2Bid' }, { name: 'description', content: 'Welcome to Best2Bid!' }];
};

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
        <script
          src="https://cdnjs.cloudflare.com/ajax/libs/webrtc-adapter/8.1.2/adapter.min.js"
          integrity="sha512-l40eBFtXx+ve5RryIELC3y6/OM6Nu89mLGQd7fg1C93tN6XrkC3supb+/YiD/Y+B8P37kdJjtG1MT1kOO2VzxA=="
          crossOrigin="anonymous"
          referrerPolicy="no-referrer"
        ></script>
      </head>
      <body>
        {children}
        <Toaster />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

const queryClient = new QueryClient();

export const ErrorBoundary = () => {
  const error = useRouteError();
  console.error(error);
  return <div>Something went wrong</div>;
};

export default function App() {
  let navigation = useNavigation();
  let fetchers = useFetchers();

  /**
   * This gets the state of every fetcher active on the app and combine it with
   * the state of the global transition (Link and Form), then use them to
   * determine if the app is idle or if it's loading.
   * Here we consider both loading and submitting as loading.
   */
  let state = useMemo<'idle' | 'loading'>(
    function getGlobalState() {
      let states = [navigation.state, ...fetchers.map((fetcher) => fetcher.state)];
      if (states.every((state) => state === 'idle')) return 'idle';
      return 'loading';
    },
    [navigation.state, fetchers],
  );

  useEffect(() => {
    // and when it's something else it means it's either submitting a form or
    // waiting for the loaders of the next location so we start it
    if (state === 'loading') NProgress.start();
    // when the state is idle then we can to complete the progress bar
    if (state === 'idle') NProgress.done();
  }, [state]);

  return (
    <QueryClientProvider client={queryClient}>
      <Outlet />
    </QueryClientProvider>
  );
}
